import { useState } from "react";
import { Send, Mail, CheckCircle } from "lucide-react";
import axios from "axios";

// Relativer Pfad → läuft über die Cloudflare Pages Function auf derselben Domain
const API = "/api";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    projectGoal: "",
    timing: "",
    budgetRange: "",
    channels: [],
    message: "",
  });

  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const budgetOptions = [
    "< 5.000€",
    "5.000€ - 10.000€",
    "10.000€ - 25.000€",
    "25.000€ - 50.000€",
    "> 50.000€",
  ];

  const channelOptions = [
    "Social Media",
    "Website",
    "TV/Cinema",
    "Events",
    "E-Commerce",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChannelToggle = (channel) => {
    setFormData((prev) => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter((c) => c !== channel)
        : [...prev.channels, channel],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Pflichtfelder, die jetzt Pills statt native Selects sind
    if (!formData.timing || !formData.budgetRange) {
      setStatus({ type: "error", message: "Bitte wähle noch Timing und Budget-Range aus." });
      return;
    }
    setLoading(true);
    setStatus({ type: "", message: "" });

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setStatus({
          type: "success",
          message: "Nachricht erfolgreich gesendet! Ich melde mich innerhalb von 24h.",
        });
        // Reset form
        setFormData({
          name: "",
          email: "",
          company: "",
          projectGoal: "",
          timing: "",
          budgetRange: "",
          channels: [],
          message: "",
        });
      }
    } catch (error) {
      setStatus({
        type: "error",
        message: "Ein Fehler ist aufgetreten. Bitte versuche es erneut oder kontaktiere mich direkt per E-Mail.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-secondary/30" data-testid="contact-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="contact-title">
              Projekt anfragen
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Lass uns über dein Projekt sprechen. Ich antworte in der Regel innerhalb von 24 Stunden.
            </p>
          </div>

          <div className="bg-background/50 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/5">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name & Email */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" htmlFor="name">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-secondary/50 border border-white/5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))] transition-all"
                    placeholder="Dein Name"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" htmlFor="email">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-secondary/50 border border-white/5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))] transition-all"
                    placeholder="deine@email.de"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              {/* Company */}
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="company">
                  Firma/Organisation
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-secondary/50 border border-white/5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))] transition-all"
                  placeholder="Optional"
                  data-testid="contact-company"
                />
              </div>

              {/* Project Goal */}
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="projectGoal">
                  Projektziel *
                </label>
                <textarea
                  id="projectGoal"
                  name="projectGoal"
                  value={formData.projectGoal}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 bg-secondary/50 border border-white/5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))] transition-all resize-none"
                  placeholder="Was möchtest du erreichen? Welche Geschichte willst du erzählen?"
                  data-testid="contact-goal"
                />
              </div>

              {/* Timing — Pills statt System-Dropdown */}
              <div>
                <label className="block text-sm font-medium mb-3">Timing *</label>
                <div className="flex flex-wrap gap-2.5" data-testid="contact-timing">
                  {[
                    { value: "asap", label: "So schnell wie möglich" },
                    { value: "1-2-weeks", label: "1–2 Wochen" },
                    { value: "1-month", label: "1 Monat" },
                    { value: "2-3-months", label: "2–3 Monate" },
                    { value: "flexible", label: "Flexibel" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, timing: opt.value }))}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                        formData.timing === opt.value
                          ? "bg-[hsl(var(--accent))] text-white"
                          : "bg-secondary/50 text-muted-foreground hover:bg-secondary border border-white/5"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget — Pills statt System-Dropdown */}
              <div>
                <label className="block text-sm font-medium mb-3">Budget-Range *</label>
                <div className="flex flex-wrap gap-2.5" data-testid="contact-budget">
                  {budgetOptions.map((budget) => (
                    <button
                      key={budget}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, budgetRange: budget }))}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                        formData.budgetRange === budget
                          ? "bg-[hsl(var(--accent))] text-white"
                          : "bg-secondary/50 text-muted-foreground hover:bg-secondary border border-white/5"
                      }`}
                    >
                      {budget}
                    </button>
                  ))}
                </div>
              </div>

              {/* Channels */}
              <div>
                <label className="block text-sm font-medium mb-3">Kanäle *</label>
                <div className="flex flex-wrap gap-3">
                  {channelOptions.map((channel) => (
                    <button
                      key={channel}
                      type="button"
                      onClick={() => handleChannelToggle(channel)}
                      className={`px-4 py-2 rounded-xl font-medium transition-all ${
                        formData.channels.includes(channel)
                          ? "bg-[hsl(var(--accent))] text-white"
                          : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
                      }`}
                      data-testid={`channel-${channel.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {channel}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Message */}
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="message">
                  Weitere Informationen
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 bg-secondary/50 border border-white/5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))] transition-all resize-none"
                  placeholder="Optional: Weitere Details zu deinem Projekt"
                  data-testid="contact-message"
                />
              </div>

              {/* Status Message */}
              {status.message && (
                <div
                  className={`p-4 rounded-xl flex items-start gap-3 ${
                    status.type === "success"
                      ? "bg-green-500/10 text-green-400 border border-green-500/20"
                      : "bg-red-500/10 text-red-400 border border-red-500/20"
                  }`}
                  data-testid="contact-status"
                >
                  {status.type === "success" && <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                  <p className="text-sm">{status.message}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full px-8 py-4 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 disabled:bg-[hsl(var(--accent))]/50 text-white rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 neon-glow"
                data-testid="contact-submit"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Anfrage senden
                  </>
                )}
              </button>

              {/* Privacy Notice */}
              <p className="text-xs text-muted-foreground text-center">
                Mit dem Absenden stimmst du der Verarbeitung deiner Daten gemäß DSGVO zu. Deine Daten werden vertraulich behandelt und nicht an Dritte weitergegeben.
              </p>
            </form>

            {/* Direct Contact */}
            <div className="mt-8 pt-8 border-t border-white/5 text-center">
              <p className="text-sm text-muted-foreground mb-3">Oder kontaktiere mich direkt:</p>
              <a
                href="mailto:kenny@freysmedia.de"
                className="inline-flex items-center gap-2 text-[hsl(var(--accent))] hover:underline font-medium"
                data-testid="direct-email"
              >
                <Mail className="w-4 h-4" />
                kenny@freysmedia.de
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
